import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import './RoomLayoutEditor.css';
import api from '../services/api';

const CHAT_API_BASE = '/api/chat';

async function authFetch(url, opts = {}) {
  const token = await api.ensureAuthenticated();
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
}

const CELL = 28;          // px per grid cell
const HANDLE_R = 6;       // radius of polygon vertex handles
const ICON_R = 14;        // radius of placed-product disc

const newItemId = () =>
  Math.random().toString(36).slice(2, 8) + Math.random().toString(36).slice(2, 8);

const RoomLayoutEditor = ({ roomId }) => {
  const svgRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [mode, setMode] = useState('place'); // 'outline' | 'place' | 'draw'
  const [drawPts, setDrawPts] = useState([]); // in-progress custom-shape vertices (cell coords)
  const [drawTargetId, setDrawTargetId] = useState(null); // item being re-drawn as custom, or null for a new one
  const resizeOrigRef = useRef(null);         // original poly points captured at resize start
  const [layout, setLayout] = useState({
    grid: { cols: 30, rows: 20 },
    outline: [],
    items: [],
    floorplan: null, // null | { url, aspect }
  });
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailProduct, setDetailProduct] = useState(null); // room product attributes shown in the info popup
  const [detailImage, setDetailImage] = useState(null);      // resolved image URL for the popup
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [draggingVertex, setDraggingVertex] = useState(null); // index
  const [draggingItemId, setDraggingItemId] = useState(null);
  const [resizingItemId, setResizingItemId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showGrid, setShowGrid] = useState(false); // grid toggle removed with the floorplan-drawing controls; keep grid off
  const fileInputRef = useRef(null);
  const [showFloorplanPicker, setShowFloorplanPicker] = useState(false);
  const [drawings, setDrawings] = useState([]);
  const [loadingDrawings, setLoadingDrawings] = useState(false);

  // Fetch the saved layout
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const res = await authFetch(`${CHAT_API_BASE}/room-layout.php?room_id=${roomId}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.success && data.data && data.data.layout) {
          const loaded = data.data.layout;
          if (!('floorplan' in loaded)) loaded.floorplan = null;
          setLayout(loaded);
          // A floorplan reads better without the grid overlaying it.
          setShowGrid(!loaded.floorplan);
        }
      } catch (e) {
        setErr('Failed to load layout');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roomId]);

  // Fetch products that belong to this room
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingProducts(true);
      try {
        const res = await authFetch(`${CHAT_API_BASE}/room-products.php?room_id=${roomId}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.success) setProducts(data.data || []);
      } catch (e) { /* ignore */ }
      if (!cancelled) setLoadingProducts(false);
    })();
    return () => { cancelled = true; };
  }, [roomId]);

  // Debounced autosave whenever layout changes
  const saveTimer = useRef(null);
  const lastSentRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const json = JSON.stringify(layout);
      if (json === lastSentRef.current) return;
      lastSentRef.current = json;
      setSaving(true);
      try {
        await authFetch(`${CHAT_API_BASE}/room-layout.php`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ room_id: roomId, layout }),
        });
      } catch (e) { /* swallow; retried on next change */ }
      setSaving(false);
    }, 600);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [layout, loading, roomId]);

  // Coordinate helpers — convert pointer event to grid coords (snapped).
  const eventToCell = useCallback((e, snap = true) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const p = pt.matrixTransform(ctm.inverse());
    const cx = p.x / CELL;
    const cy = p.y / CELL;
    return snap
      ? { x: Math.round(cx), y: Math.round(cy) }
      : { x: cx, y: cy };
  }, []);

  // Import a PDF (or image) floorplan: upload it, then store the rendered PNG
  // URL on the layout and match the grid's row count to the image aspect ratio
  // so the background isn't stretched.
  const handleFloorplanFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('room_id', roomId);
      // No explicit Content-Type — the browser sets the multipart boundary.
      const res = await authFetch(`${CHAT_API_BASE}/room-floorplan-upload.php`, {
        method: 'POST',
        body: form,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Upload failed');
      }
      applyFloorplanData(data.data);
    } catch (err) {
      alert('Failed to import floorplan: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  // Set the floorplan background from an upload/drawing result and match the
  // grid's row count to the image aspect so it isn't stretched.
  const applyFloorplanData = (d) => {
    const aspect = Number(d.aspect) || 0;
    setLayout(l => {
      const cols = l.grid.cols;
      const rows = aspect > 0 ? clamp(Math.round(cols * aspect), 4, 60) : l.grid.rows;
      return { ...l, floorplan: { url: d.url, aspect }, grid: { ...l.grid, rows } };
    });
    setShowGrid(false);
    setMode('place');
  };

  // Open the "choose a floorplan" picker and load this room's drawings.
  const openFloorplanPicker = async () => {
    setShowFloorplanPicker(true);
    setLoadingDrawings(true);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/room-drawings.php?room_id=${roomId}`);
      const data = await res.json();
      setDrawings(data.success ? (data.data || []) : []);
    } catch (e) { setDrawings([]); }
    setLoadingDrawings(false);
  };

  // Use an existing room Drawing as the floorplan (server renders PDF -> PNG).
  const handleUseDrawing = async (drawing) => {
    let src = (drawing.attributes?.drawing_url ?? drawing.drawing_url) || '';
    const idx = src.indexOf('/upload/');
    if (idx >= 0) src = src.slice(idx);
    setShowFloorplanPicker(false);
    setImporting(true);
    try {
      const form = new FormData();
      form.append('room_id', roomId);
      form.append('source_url', src);
      const res = await authFetch(`${CHAT_API_BASE}/room-floorplan-upload.php`, { method: 'POST', body: form });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load drawing');
      applyFloorplanData(data.data);
    } catch (err) {
      alert('Failed to use drawing as floorplan: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const removeFloorplan = () => {
    setLayout(l => ({ ...l, floorplan: null }));
    setShowGrid(true);
  };

  // Outline mode: single click does NOT add a vertex (only deselects); a
  // double-click adds the vertex. This stops accidental vertices being
  // dropped when the user is just trying to drag an existing one.
  // Place mode: single click drops the currently picked product.
  // Finish the in-progress custom shape: convert the drawn vertices into a
  // 'poly' marker whose points are offsets (px) from the shape's centroid.
  const finishDrawing = () => {
    if (drawPts.length < 3) return;
    const cx = drawPts.reduce((s, p) => s + p.x, 0) / drawPts.length;
    const cy = drawPts.reduce((s, p) => s + p.y, 0) / drawPts.length;
    const points = drawPts.map(p => ({ x: (p.x - cx) * CELL, y: (p.y - cy) * CELL }));
    const xs = points.map(p => p.x), ys = points.map(p => p.y);
    const w = Math.max(...xs) - Math.min(...xs);
    const h = Math.max(...ys) - Math.min(...ys);
    if (drawTargetId) {
      // Re-drawing an existing marker's shape (keeps its id/product/label/colour).
      setLayout(l => ({
        ...l,
        items: l.items.map(it => it.id === drawTargetId ? { ...it, x: cx, y: cy, shape: 'poly', points, w, h } : it),
      }));
      setSelectedItemId(drawTargetId);
      setDrawTargetId(null);
    } else {
      const newItem = {
        id: newItemId(),
        product_id: selectedProduct ? selectedProduct.id : null,
        label: selectedProduct ? selectedProduct.label : 'Shape',
        color: selectedProduct ? selectedProduct.color : '#c2b280',
        x: cx, y: cy, shape: 'poly', points, w, h,
      };
      setLayout(l => ({ ...l, items: [...l.items, newItem] }));
      setSelectedItemId(newItem.id);
    }
    setDrawPts([]);
    setMode('place');
  };

  const handleSvgClick = (e) => {
    if (draggingVertex !== null || draggingItemId) return;
    if (mode === 'draw') {
      const p = eventToCell(e);
      // Click near the first vertex (with >= 3 points) closes the shape.
      if (drawPts.length >= 3) {
        const f = drawPts[0];
        if (Math.abs(p.x - f.x) < 0.6 && Math.abs(p.y - f.y) < 0.6) { finishDrawing(); return; }
      }
      setDrawPts(pts => [...pts, p]);
      return;
    }
    if (mode === 'outline') {
      // No-op on single click; selection state isn't used in outline mode.
      return;
    } else if (mode === 'place' && selectedProduct) {
      const p = eventToCell(e);
      const newItem = {
        id: newItemId(),
        product_id: selectedProduct.id,
        label: selectedProduct.label,
        color: selectedProduct.color,
        x: p.x, y: p.y,
        shape: 'circle',      // 'circle' | 'rect'
        w: ICON_R * 2,        // marker width  (circle diameter)
        h: ICON_R * 2,        // marker height (circle diameter)
      };
      setLayout(l => ({ ...l, items: [...l.items, newItem] }));
      setSelectedItemId(newItem.id);
      // Keep the product selected so users can drop multiple of the same.
    } else if (mode === 'place') {
      // Clicking empty canvas deselects
      setSelectedItemId(null);
    }
  };

  const handleSvgDoubleClick = (e) => {
    if (mode === 'draw') { finishDrawing(); return; }
    if (mode !== 'outline') return;
    if (draggingVertex !== null || draggingItemId) return;
    const p = eventToCell(e);
    const inBounds = p.x >= 0 && p.y >= 0 && p.x <= layout.grid.cols && p.y <= layout.grid.rows;
    if (!inBounds) return;
    // Double-clicking the first vertex is a no-op (polygon is auto-closed).
    const first = layout.outline[0];
    if (layout.outline.length >= 3 && first && Math.abs(p.x - first.x) < 0.5 && Math.abs(p.y - first.y) < 0.5) {
      return;
    }
    setLayout(l => ({ ...l, outline: [...l.outline, p] }));
  };

  // Drag handlers
  const onPointerMove = useCallback((e) => {
    if (draggingVertex !== null) {
      const p = eventToCell(e);
      setLayout(l => {
        const next = [...l.outline];
        next[draggingVertex] = p;
        return { ...l, outline: next };
      });
    } else if (draggingItemId) {
      const p = eventToCell(e, false);
      setLayout(l => ({
        ...l,
        items: l.items.map(it => it.id === draggingItemId ? { ...it, x: p.x, y: p.y } : it),
      }));
    } else if (resizingItemId) {
      // Resize from the corner handle: size = distance from the marker centre.
      const c = eventToCell(e, false);
      setLayout(l => ({
        ...l,
        items: l.items.map(it => {
          if (it.id !== resizingItemId) return it;
          const dxPx = Math.abs((c.x - it.x) * CELL);
          const dyPx = Math.abs((c.y - it.y) * CELL);
          if (it.shape === 'poly' && resizeOrigRef.current && Array.isArray(resizeOrigRef.current.points)) {
            // Scale the drawn outline uniformly from the original points.
            const op = resizeOrigRef.current.points;
            const oDist = Math.hypot(Math.max(...op.map(p => p.x)), Math.max(...op.map(p => p.y))) || 1;
            const scale = Math.max(0.2, Math.min(6, Math.hypot(dxPx, dyPx) / oDist));
            const points = op.map(p => ({ x: p.x * scale, y: p.y * scale }));
            const xs = points.map(p => p.x), ys = points.map(p => p.y);
            return { ...it, points, w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
          }
          if ((it.shape || 'circle') === 'rect') {
            return { ...it, w: Math.max(12, dxPx * 2), h: Math.max(12, dyPx * 2) };
          }
          const r = Math.max(8, Math.hypot(dxPx, dyPx)); // circles stay round
          return { ...it, w: r * 2, h: r * 2 };
        }),
      }));
    }
  }, [draggingVertex, draggingItemId, resizingItemId, eventToCell]);

  const onPointerUp = useCallback(() => {
    setDraggingVertex(null);
    setDraggingItemId(null);
    setResizingItemId(null);
  }, []);

  useEffect(() => {
    if (draggingVertex === null && draggingItemId === null && resizingItemId === null) return;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [draggingVertex, draggingItemId, resizingItemId, onPointerMove, onPointerUp]);

  // Keyboard delete
  useEffect(() => {
    const onKey = (e) => {
      // Don't grab when user is typing in an input
      const t = e.target;
      const tag = (t && t.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (t && t.isContentEditable)) return;
      if (mode === 'draw') {
        if (e.key === 'Escape') { e.preventDefault(); setDrawPts([]); setDrawTargetId(null); setMode('place'); }
        else if (e.key === 'Backspace' || e.key === 'Delete') { e.preventDefault(); setDrawPts(p => p.slice(0, -1)); }
        return;
      }
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (selectedItemId) {
        e.preventDefault();
        setLayout(l => ({ ...l, items: l.items.filter(it => it.id !== selectedItemId) }));
        setSelectedItemId(null);
      } else if (mode === 'outline' && layout.outline.length > 0) {
        e.preventDefault();
        setLayout(l => ({ ...l, outline: l.outline.slice(0, -1) }));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedItemId, mode, layout.outline.length]);

  // Derive product-list display
  const productItems = useMemo(() => {
    // Map each product to its first placed marker (if any) so the list icon can
    // mirror the on-canvas marker's text/color.
    const placedByProduct = {};
    (layout.items || []).forEach(it => {
      if (it.product_id && !placedByProduct[it.product_id]) placedByProduct[it.product_id] = it;
    });
    return products.map(p => {
      const a = p.attributes || {};
      const label = a.client_facing_name_c || a.name || '(unnamed)';
      const sku = a.part_number || a.sku_c || '';
      const placed = placedByProduct[p.id];
      // Icon text/color match the placed marker (which is derived from the
      // product label at placement, or a custom marker label); fall back to the
      // product label when the product isn't placed on the layout.
      const iconLabel = (placed && placed.label) ? placed.label : label;
      const color = (placed && placed.color) ? placed.color : colorForKey(label);
      return { id: p.id, label, sku, color, iconLabel };
    });
  }, [products, layout.items]);

  // Double-click a room product to show its picture + details. The image (when
  // any) comes from the linked catalog product via product-images.php.
  const openProductDetail = useCallback(async (productId) => {
    const p = products.find(x => x.id === productId);
    if (!p) return;
    const a = p.attributes || {};
    setDetailProduct(a);
    setDetailImage(null);
    const srcId = a.source_product_id;
    if (srcId) {
      setDetailLoading(true);
      try {
        const res = await authFetch(`${CHAT_API_BASE}/product-images.php?product_id=${encodeURIComponent(srcId)}`);
        const data = await res.json();
        const imgs = (data && data.images) || [];  // product-images.php returns { images: [...] }
        if (imgs.length && imgs[0].image_path) setDetailImage('/' + String(imgs[0].image_path).replace(/^\/+/, ''));
        else if (imgs.length && imgs[0].source_url) setDetailImage(imgs[0].source_url);
      } catch (e) { /* no image available */ }
      setDetailLoading(false);
    }
  }, [products]);

  // Close the detail popup on Escape.
  useEffect(() => {
    if (!detailProduct) return;
    const onKey = (e) => { if (e.key === 'Escape') setDetailProduct(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detailProduct]);

  // Grid lines
  const gridLines = useMemo(() => {
    const lines = [];
    for (let i = 0; i <= layout.grid.cols; i++) {
      lines.push(<line key={`v${i}`} x1={i * CELL} y1={0} x2={i * CELL} y2={layout.grid.rows * CELL} />);
    }
    for (let j = 0; j <= layout.grid.rows; j++) {
      lines.push(<line key={`h${j}`} x1={0} y1={j * CELL} x2={layout.grid.cols * CELL} y2={j * CELL} />);
    }
    return lines;
  }, [layout.grid.cols, layout.grid.rows]);

  const polygonPoints = layout.outline.map(p => `${p.x * CELL},${p.y * CELL}`).join(' ');
  const widthPx = layout.grid.cols * CELL;
  const heightPx = layout.grid.rows * CELL;

  const updateItem = (id, patch) => setLayout(l => ({
    ...l, items: l.items.map(it => it.id === id ? { ...it, ...patch } : it),
  }));
  const removeItem = (id) => {
    setLayout(l => ({ ...l, items: l.items.filter(it => it.id !== id) }));
    setSelectedItemId(null);
  };
  const selectedItem = layout.items.find(it => it.id === selectedItemId) || null;

  return (
    <div className="room-layout-editor">
      <div className="layout-toolbar">
        <div className="layout-mode-group">
          {/* "Edit Outline" (floorplan drawing) removed — uncomment to restore.
          <button
            className={`layout-mode-btn ${mode === 'outline' ? 'active' : ''}`}
            onClick={() => { setMode('outline'); setSelectedProduct(null); }}
          >Edit Outline</button>
          */}
          <button
            className={`layout-mode-btn ${mode === 'place' ? 'active' : ''}`}
            onClick={() => setMode('place')}
          >Place Products</button>
        </div>
        {/* Grid "Cols"/"Rows" controls removed with the floorplan-drawing features — uncomment to restore.
        <div className="layout-grid-controls">
          <label>Cols
            <input type="number" min="4" max="80" value={layout.grid.cols}
              onChange={e => setLayout(l => ({ ...l, grid: { ...l.grid, cols: clamp(parseInt(e.target.value, 10) || 0, 4, 80) } }))} />
          </label>
          <label>Rows
            <input type="number" min="4" max="60" value={layout.grid.rows}
              onChange={e => setLayout(l => ({ ...l, grid: { ...l.grid, rows: clamp(parseInt(e.target.value, 10) || 0, 4, 60) } }))} />
          </label>
        </div>
        */}
        <div className="layout-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/*"
            style={{ display: 'none' }}
            onChange={handleFloorplanFile}
          />
          <button
            className="layout-action-btn layout-action-import"
            disabled={importing}
            onClick={openFloorplanPicker}
            title="Choose a floorplan from this room's drawings or upload one"
          >
            {importing ? 'Importing…' : layout.floorplan ? 'Replace Floorplan' : 'Import Floorplan'}
          </button>
          {layout.floorplan && (
            <>
              <button className="layout-action-btn" onClick={removeFloorplan}>
                Remove Floorplan
              </button>
              {/* "Show Grid" toggle removed with the floorplan-drawing features — uncomment to restore.
              <button
                className={`layout-action-btn ${showGrid ? 'active' : ''}`}
                onClick={() => setShowGrid(g => !g)}
                title="Show or hide the grid over the floorplan"
              >
                {showGrid ? 'Hide Grid' : 'Show Grid'}
              </button>
              */}
            </>
          )}
          {mode === 'outline' && (
            <button className="layout-action-btn" onClick={() => setLayout(l => ({ ...l, outline: [] }))}>
              Clear Outline
            </button>
          )}
          {mode === 'place' && (
            <>
              <button className="layout-action-btn"
                onClick={() => { setSelectedItemId(null); setDrawTargetId(null); setDrawPts([]); setMode('draw'); }}
                title="Draw a custom marker outline (e.g. an L-shaped sectional)">
                ✎ Draw Shape
              </button>
              <button className="layout-action-btn" onClick={() => setLayout(l => ({ ...l, items: [] }))}>
                Clear All Items
              </button>
            </>
          )}
          {mode === 'draw' && (
            <>
              <button className="layout-action-btn layout-action-primary"
                onClick={finishDrawing} disabled={drawPts.length < 3}>
                ✓ Finish Shape
              </button>
              <button className="layout-action-btn" onClick={() => { setDrawPts([]); setDrawTargetId(null); setMode('place'); }}>
                Cancel
              </button>
            </>
          )}
          <button className="layout-action-btn layout-action-pdf" onClick={async () => {
            try {
              // Force a save first so the PDF reflects the latest in-memory state.
              const json = JSON.stringify(layout);
              if (json !== lastSentRef.current) {
                setSaving(true);
                try {
                  await authFetch(`${CHAT_API_BASE}/room-layout.php`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ room_id: roomId, layout }),
                  });
                  lastSentRef.current = json;
                } finally { setSaving(false); }
              }
              const res = await authFetch(`${CHAT_API_BASE}/room-layout-pdf.php?room_id=${roomId}`);
              if (!res.ok) throw new Error('PDF generation failed');
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `Room_Layout_${roomId}.pdf`;
              a.click();
              window.URL.revokeObjectURL(url);
            } catch (e) {
              alert('Failed to generate layout PDF: ' + e.message);
            }
          }}>
            Download PDF
          </button>
          <span className={`layout-save-status ${saving ? 'saving' : 'saved'}`}>
            {saving ? 'Saving…' : 'Saved'}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="layout-loading">Loading layout…</div>
      ) : err ? (
        <div className="layout-error">{err}</div>
      ) : (
        <div className="layout-main">
          <div className="layout-canvas-wrap">
            <p className="layout-help">
              {mode === 'draw'
                ? `Click to add each corner of the shape${selectedProduct ? ` for "${selectedProduct.label}"` : ''}. Click the first point (or double-click) to finish. Backspace undoes a point; Esc cancels.`
                : mode === 'outline'
                ? 'Double-click the grid to add a wall corner. Drag corners to reshape. Backspace removes the last point.'
                : selectedProduct
                  ? `Click in the room to drop "${selectedProduct.label}". Click a placed icon to select; drag to move; Delete removes it.`
                  : 'Pick a product from the right column, then click in the room to drop it.'}
            </p>
            <div className="layout-canvas-holder">
            <svg
              ref={svgRef}
              className={`layout-canvas mode-${mode}`}
              width={widthPx}
              height={heightPx}
              viewBox={`0 0 ${widthPx} ${heightPx}`}
              preserveAspectRatio="xMidYMid meet"
              onClick={handleSvgClick}
              onDoubleClick={handleSvgDoubleClick}
            >
              {layout.floorplan && layout.floorplan.url && (
                <image
                  className="layout-floorplan-img"
                  href={layout.floorplan.url}
                  xlinkHref={layout.floorplan.url}
                  x={0}
                  y={0}
                  width={widthPx}
                  height={heightPx}
                  preserveAspectRatio="none"
                />
              )}
              {showGrid && <g className="grid-lines">{gridLines}</g>}
              {layout.outline.length >= 3 && (
                <polygon
                  className="layout-polygon"
                  points={polygonPoints}
                />
              )}
              {layout.outline.length >= 2 && layout.outline.length < 3 && (
                <polyline
                  className="layout-polyline"
                  points={polygonPoints}
                />
              )}
              {mode === 'outline' && layout.outline.map((p, i) => (
                <circle
                  key={`vh-${i}`}
                  className={`vertex-handle ${i === 0 ? 'first' : ''}`}
                  cx={p.x * CELL}
                  cy={p.y * CELL}
                  r={HANDLE_R}
                  onPointerDown={(e) => { e.stopPropagation(); setDraggingVertex(i); }}
                />
              ))}
              {layout.items.map(it => (
                <g
                  key={it.id}
                  className={`layout-item ${selectedItemId === it.id ? 'selected' : ''}`}
                  transform={`translate(${it.x * CELL}, ${it.y * CELL})`}
                  // While drawing a custom shape, let clicks fall through to the
                  // canvas so vertices can be placed anywhere (even over a marker).
                  style={{ pointerEvents: mode === 'draw' ? 'none' : undefined }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setSelectedItemId(it.id);
                    setDraggingItemId(it.id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => { e.stopPropagation(); openProductDetail(it.product_id); }}
                >
                  {(() => {
                    const shape = it.shape || 'circle';
                    // Custom drawn shape (e.g. an L-shaped sectional).
                    if (shape === 'poly' && Array.isArray(it.points) && it.points.length >= 2) {
                      const maxX = Math.max(...it.points.map(p => p.x));
                      const maxY = Math.max(...it.points.map(p => p.y));
                      return (
                        <>
                          <polygon points={it.points.map(p => `${p.x},${p.y}`).join(' ')}
                            fill={it.color} stroke="#1a1a1a" strokeWidth="1" strokeLinejoin="round" />
                          <text y="4" textAnchor="middle" fontSize="11" fontWeight="700" fill="#1a1a1a">
                            {initials(it.label)}
                          </text>
                          <title>{it.label}</title>
                          <text y={maxY + 12} textAnchor="middle" fontSize="10" fill="#ddd">
                            {truncate(it.label, 18)}
                          </text>
                          {selectedItemId === it.id && (
                            <rect className="layout-item-resize" x={maxX - 5} y={maxY - 5} width="10" height="10"
                              onPointerDown={(e) => { e.stopPropagation(); setSelectedItemId(it.id); resizeOrigRef.current = { points: it.points }; setResizingItemId(it.id); }} />
                          )}
                        </>
                      );
                    }
                    const w = it.w || ICON_R * 2;
                    const h = it.h || ICON_R * 2;
                    const halfW = w / 2;
                    const halfH = shape === 'rect' ? h / 2 : halfW;
                    return (
                      <>
                        {shape === 'rect' ? (
                          <rect x={-halfW} y={-halfH} width={w} height={h} rx="3"
                            fill={it.color} stroke="#1a1a1a" strokeWidth="1" />
                        ) : (
                          <circle r={halfW} fill={it.color} stroke="#1a1a1a" strokeWidth="1" />
                        )}
                        <text y="4" textAnchor="middle" fontSize="11" fontWeight="700" fill="#1a1a1a">
                          {initials(it.label)}
                        </text>
                        <title>{it.label}</title>
                        <text y={halfH + 12} textAnchor="middle" fontSize="10" fill="#ddd">
                          {truncate(it.label, 18)}
                        </text>
                        {selectedItemId === it.id && (
                          <rect
                            className="layout-item-resize"
                            x={halfW - 5}
                            y={halfH - 5}
                            width="10"
                            height="10"
                            onPointerDown={(e) => { e.stopPropagation(); setSelectedItemId(it.id); setResizingItemId(it.id); }}
                          />
                        )}
                      </>
                    );
                  })()}
                </g>
              ))}
              {mode === 'draw' && drawPts.length > 0 && (
                <g className="layout-draw-preview" pointerEvents="none">
                  <polygon points={drawPts.map(p => `${p.x * CELL},${p.y * CELL}`).join(' ')}
                    fill="rgba(194,178,128,0.18)" stroke="#c2b280" strokeWidth="2" strokeDasharray="5 4" strokeLinejoin="round" />
                  {drawPts.map((p, i) => (
                    <circle key={i} cx={p.x * CELL} cy={p.y * CELL} r={i === 0 ? 6 : 4}
                      fill={i === 0 ? '#c2b280' : '#fff'} stroke="#1a1a1a" strokeWidth="1" />
                  ))}
                </g>
              )}
            </svg>
            </div>
          </div>

          <aside className="layout-product-column">
            {selectedItem && (() => {
              const shape = selectedItem.shape || 'circle';
              const w = Math.round(selectedItem.w || ICON_R * 2);
              const h = Math.round(selectedItem.h || ICON_R * 2);
              const drawingThis = mode === 'draw' && drawTargetId === selectedItem.id;
              const exitDraw = () => { if (drawingThis) { setMode('place'); setDrawTargetId(null); setDrawPts([]); } };
              return (
                <div className="layout-selected-panel">
                  <div className="layout-selected-head">
                    <span className="layout-selected-swatch" style={{ background: selectedItem.color }} />
                    <span className="layout-selected-name">{selectedItem.label}</span>
                  </div>
                  <div className="layout-selected-row">
                    <span className="layout-selected-lbl">Label</span>
                    <input
                      type="text"
                      className="layout-selected-label-input"
                      value={selectedItem.label || ''}
                      onChange={(e) => updateItem(selectedItem.id, { label: e.target.value })}
                      placeholder="Marker label"
                    />
                  </div>
                  <div className="layout-selected-row">
                    <span className="layout-selected-lbl">Shape</span>
                    <div className="layout-shape-toggle">
                      <button
                        className={`layout-shape-btn ${!drawingThis && shape === 'circle' ? 'active' : ''}`}
                        title="Circle" aria-label="Circle"
                        onClick={() => { exitDraw(); updateItem(selectedItem.id, { shape: 'circle', w: Math.max(12, w), h: Math.max(12, w) }); }}
                      >●</button>
                      <button
                        className={`layout-shape-btn ${!drawingThis && shape === 'rect' ? 'active' : ''}`}
                        title="Rectangle" aria-label="Rectangle"
                        onClick={() => { exitDraw(); updateItem(selectedItem.id, { shape: 'rect', w: Math.max(12, w), h: Math.max(12, h) }); }}
                      >▭</button>
                      <button
                        className={`layout-shape-btn ${drawingThis || shape === 'poly' ? 'active' : ''}`}
                        title="Custom — draw a shape" aria-label="Custom shape"
                        onClick={() => { setSelectedItemId(selectedItem.id); setDrawTargetId(selectedItem.id); setDrawPts([]); setMode('draw'); }}
                      >✎</button>
                    </div>
                  </div>
                  <div className="layout-selected-row">
                    <span className="layout-selected-lbl">Size</span>
                    {drawingThis ? (
                      <span className="layout-draw-note">Click each corner on the plan, then <strong>✓ Finish Shape</strong> (Esc cancels).</span>
                    ) : shape === 'poly' ? (
                      <input
                        type="range" min="20" max="400" value={w}
                        onChange={(e) => {
                          const v = +e.target.value;
                          const cur = selectedItem.w || 1;
                          const s = v / cur;
                          if (!isFinite(s) || s <= 0) return;
                          const points = (selectedItem.points || []).map(p => ({ x: p.x * s, y: p.y * s }));
                          const xs = points.map(p => p.x), ys = points.map(p => p.y);
                          updateItem(selectedItem.id, { points, w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) });
                        }}
                      />
                    ) : shape === 'circle' ? (
                      <input
                        type="range" min="12" max="160" value={w}
                        onChange={(e) => { const v = +e.target.value; updateItem(selectedItem.id, { w: v, h: v }); }}
                      />
                    ) : (
                      <div className="layout-size-inputs">
                        <label>W<input type="number" min="12" max="600" value={w}
                          onChange={(e) => updateItem(selectedItem.id, { w: Math.max(12, +e.target.value || 12) })} /></label>
                        <label>H<input type="number" min="12" max="600" value={h}
                          onChange={(e) => updateItem(selectedItem.id, { h: Math.max(12, +e.target.value || 12) })} /></label>
                      </div>
                    )}
                  </div>
                  <button className="layout-selected-delete" onClick={() => removeItem(selectedItem.id)}>Remove from layout</button>
                  <p className="layout-selected-hint">Tip: drag the corner handle on the marker to resize.</p>
                </div>
              );
            })()}
            <h4>Room Products</h4>
            {loadingProducts ? (
              <p className="layout-empty">Loading…</p>
            ) : productItems.length === 0 ? (
              <p className="layout-empty">No products are assigned to this room yet.</p>
            ) : (
              <ul className="layout-product-list">
                {productItems.map(p => {
                  const isSelected = selectedProduct?.id === p.id;
                  return (
                    <li
                      key={p.id}
                      className={`layout-product-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => { setSelectedProduct(isSelected ? null : p); setMode('place'); }}
                      onDoubleClick={() => openProductDetail(p.id)}
                      title={`${p.label} — double-click for details`}
                    >
                      <span className="layout-product-icon" style={{ background: p.color }}>
                        {initials(p.iconLabel)}
                      </span>
                      <span className="layout-product-text">
                        <span className="layout-product-name">{p.label}</span>
                        {p.sku && <span className="layout-product-sku">{p.sku}</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="layout-product-hint">
              {selectedProduct
                ? <>Selected: <strong>{selectedProduct.label}</strong>. Click in the room to drop it.</>
                : <>Click a product to pick it up, then click in the room.</>}
            </p>
          </aside>
        </div>
      )}

      {showFloorplanPicker && (() => {
        // room-drawings.php returns { id, attributes: {...} }, so read fields
        // from attributes. Only same-site /upload/ files can be rendered.
        const usable = drawings
          .map(d => ({ id: d.id, ...(d.attributes || {}) }))
          .filter(d => d.drawing_url && d.drawing_url.indexOf('/upload/') >= 0);
        return (
          <div className="layout-fp-overlay" onClick={() => setShowFloorplanPicker(false)}>
            <div className="layout-fp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="layout-fp-head">
                <h3>Choose a floorplan</h3>
                <button className="layout-fp-close" onClick={() => setShowFloorplanPicker(false)} aria-label="Close">×</button>
              </div>
              <p className="layout-fp-sub">Pick a drawing from this room, or upload one from your computer.</p>
              <div className="layout-fp-body">
                {loadingDrawings ? (
                  <p className="layout-empty">Loading drawings…</p>
                ) : usable.length === 0 ? (
                  <p className="layout-empty">No uploaded drawings in this room yet.</p>
                ) : (
                  <ul className="layout-fp-list">
                    {usable.map(d => {
                      const isPdf = /\.pdf(\?|$)/i.test(d.drawing_url);
                      return (
                        <li key={d.id} className="layout-fp-item" onClick={() => handleUseDrawing(d)}>
                          <span className="layout-fp-icon">{isPdf ? '📄' : '🖼️'}</span>
                          <span className="layout-fp-name">{d.name || d.drawing_url.split('/').pop()}</span>
                          {d.drawing_type && <span className="layout-fp-type">{d.drawing_type}</span>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="layout-fp-actions">
                <button
                  className="layout-action-btn layout-action-import"
                  onClick={() => { setShowFloorplanPicker(false); fileInputRef.current && fileInputRef.current.click(); }}
                >⬆ Upload from computer</button>
              </div>
            </div>
          </div>
        );
      })()}

      {detailProduct && (() => {
        const d = detailProduct;
        const money = (v) => (v === null || v === undefined || v === '' || isNaN(Number(v)))
          ? '' : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const hasDim = [d.width_c, d.depth_c, d.height_c].some(x => x !== null && x !== undefined && x !== '');
        const dims = hasDim ? `${d.width_c || '—'} W × ${d.depth_c || '—'} D × ${d.height_c || '—'} H in` : '';
        const rows = [
          ['Name', d.name],
          ['SKU', d.part_number],
          ['Category', d.category],
          ['Manufacturer', d.manufacturer_c],
          ['Vendor', d.vendor_c],
          ['Color', d.color_c],
          ['Finish', d.finish_c],
          ['Material', d.material_c],
          ['Dimensions', dims],
          ['Quantity', (d.quantity !== null && d.quantity !== undefined && d.quantity !== '') ? String(d.quantity) : ''],
          ['Price', money(d.price)],
          ['Cost', money(d.cost)],
        ].filter(([, v]) => v !== null && v !== undefined && v !== '');
        return (
          <div className="rp-modal-overlay" onClick={() => setDetailProduct(null)}>
            <div className="rp-modal" onClick={e => e.stopPropagation()}>
              <button className="rp-modal-close" onClick={() => setDetailProduct(null)} aria-label="Close">×</button>
              <h3 className="rp-modal-title">{d.client_facing_name_c || d.name || 'Product'}</h3>
              <div className="rp-modal-body">
                <div className="rp-modal-img">
                  {detailLoading
                    ? <div className="rp-modal-img-ph">Loading image…</div>
                    : detailImage
                      ? <img src={detailImage} alt={d.name || ''} onError={(e) => { e.target.style.display = 'none'; }} />
                      : <div className="rp-modal-img-ph">No image</div>}
                </div>
                <dl className="rp-modal-details">
                  {rows.map(([label, val]) => (
                    <React.Fragment key={label}>
                      <dt>{label}</dt><dd>{val}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
              {d.description && (
                <div className="rp-modal-desc">
                  <h4>Description</h4>
                  <p>{d.description}</p>
                </div>
              )}
              {d.url && (
                <p className="rp-modal-url">
                  <a href={d.url} target="_blank" rel="noopener noreferrer">Open product link ↗</a>
                </p>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

function initials(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/).slice(0, 2);
  const out = words.map(w => w[0]).join('').toUpperCase();
  return out || name[0].toUpperCase();
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

// Stable color per product label so the same product gets the same icon color.
function colorForKey(key) {
  const palette = [
    '#c2b280', '#a89968', '#cf8b73', '#7a9b8e', '#9f86c0',
    '#d4a373', '#bfa3a3', '#8da08a', '#c79a6b', '#9babbf',
  ];
  let h = 0;
  for (let i = 0; i < (key || '').length; i++) {
    h = (h * 31 + key.charCodeAt(i)) >>> 0;
  }
  return palette[h % palette.length];
}

export default RoomLayoutEditor;
